import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Smartphone, Settings, BarChart2, ArrowRight } from 'lucide-react';
import PageLayout, { PageHero } from '../components/PageLayout';
import Accordion from '../components/Accordion';

const CATEGORIES = [
  {
    icon: Smartphone,
    accent: 'accent-1',
    label: 'Getting started',
    faqs: [
      { q: 'How do I set up WASLX?', a: 'Sign up on our website, add your agents, upload your lead list as a CSV, and your team can start calling within 30 minutes. No hardware or IT team required.' },
      { q: 'What Android version does the app require?', a: 'WASLX works on any Android smartphone running Android 8.0 (Oreo) or later. Agents use their own SIM card — no additional hardware needed.' },
      { q: 'How do agents install the app?', a: 'Download the APK directly from our website and install it on the agent’s Android phone. Enable “Install from unknown sources” in Android settings if prompted.' },
      { q: 'Can agents work remotely?', a: 'Yes. WASLX is mobile-first by design — agents can dial from anywhere they have a phone signal. No office and no desk phone required.' },
    ],
  },
  {
    icon: Settings,
    accent: 'accent-5',
    label: 'Campaigns & leads',
    faqs: [
      { q: 'How do I import leads?', a: 'Go to Lead Management → Import, upload your CSV, match each column to a WASLX field, and choose how to handle duplicates. WASLX shows a summary of imported, skipped and errored rows.' },
      { q: 'Can I run multiple campaigns at once?', a: 'Yes. WASLX supports multiple concurrent campaigns. Each campaign has its own lead list, agents, dialing rules and reporting.' },
      { q: 'What is a disposition?', a: 'A disposition is the outcome of a call — for example Answered, No Answer, Sale, or Callback. You define your own dispositions in Settings → Dispositions before creating campaigns.' },
      { q: 'What is a Do Not Call (DNC) list?', a: 'The DNC list contains numbers your team must never dial. When a customer asks to be removed, update the lead status and inform your admin to add the number to DNC.' },
    ],
  },
  {
    icon: BarChart2,
    accent: 'accent-6',
    label: 'Monitoring & reports',
    faqs: [
      { q: 'How do I monitor agents in real time?', a: 'Open the Dashboard from the left menu. It updates every few seconds, showing which agents are live, idle or on break, along with current call counts and connect rates.' },
      { q: 'Can supervisors listen to calls?', a: 'Live monitoring is available on the Dashboard. Call recordings, where enabled, can be played back from Campaign Management → Call logs, subject to your role permissions.' },
      { q: 'How do I export a call report?', a: 'From Campaign Management → Call logs, select the campaign and date range, then click Export CSV. The file includes call duration, agent, lead, outcome and timestamp.' },
      { q: 'Where do I see per-agent performance?', a: 'Analytics in the left menu shows agent scorecards and per-campaign breakdowns. Agents can also view their own numbers from the Reports tab in the mobile app.' },
    ],
  },
];

const HelpCenter = () => (
  <PageLayout
    title="Help Center — FAQs & Support"
    description="Answers to common WASLX questions — app setup, lead import, campaign management, live monitoring, analytics, and billing. Can't find it? Contact our team directly."
    path="/help"
  >
    <PageHero
      eyebrow="Help center"
      title={
        <>
          How can we <span className="display-accent">help you?</span>
        </>
      }
      sub="Answers to the most common questions about WASLX. Cannot find what you need? Talk to us directly."
    />

    <section className="section section--muted">
      <div className="container-page">
        <div className="mx-auto max-w-3xl space-y-12">
          {CATEGORIES.map(category => {
            const Icon = category.icon;
            return (
              <div key={category.label} className={category.accent}>
                <div className="mb-6 flex items-center gap-3">
                  <span className="icon-tile">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-content">{category.label}</h2>
                </div>
                <Accordion items={category.faqs} idPrefix={category.label.replace(/\W+/g, '-')} />
              </div>
            );
          })}

          <div className="accent-brand card card--tint text-center">
            <span className="icon-tile icon-tile--lg icon-tile--solid mx-auto">
              <HelpCircle className="h-6 w-6" />
            </span>
            <h3 className="mt-4 text-xl font-bold text-content">Still need help?</h3>
            <p className="mx-auto mt-2 max-w-md text-[0.9375rem] text-content-muted">
              Our team responds within one business day. During the beta you talk directly to the
              people building the product.
            </p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/contact" className="btn-pill btn-pill--primary">
                Contact support
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/documentation" className="btn-pill">
                Read the docs
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </PageLayout>
);

export default HelpCenter;
